/**
 * App Context
 * Provides the dependency injection container to all React components
 *
 * Usage in components:
 *   import { useApp } from '../context/AppContext'
 *
 *   function MyComponent() {
 *     const { db, files, api } = useApp()
 *     const apps = await db.getApplications()
 *   }
 */

import { createContext, useContext, useMemo, useState, useEffect } from 'react'
import { getContainer } from '../bootstrap'

// Create the context
const AppContext = createContext(null)

/**
 * App Provider Component
 * Wrap your app with this to provide access to the container
 * Loads settings from database on mount (or uses provided config)
 *
 * @param {Object} config - Optional configuration for embedded mode
 * @param {string} config.apiBaseUrl - API base URL (overrides database)
 * @param {string} config.userEmail - User email for multi-tenant tracking
 * @param {string} config.authToken - JWT token for API authentication
 * @param {string} config.accountId - Account ID for multi-tenant operations
 */
export function AppProvider({ children, config }) {
  // Get the singleton container
  const container = useMemo(() => getContainer(), [])
  const [settingsLoaded, setSettingsLoaded] = useState(false)

  // Load settings from database and check URL params for user context
  useEffect(() => {
    const loadSettings = async () => {
      try {
        // Always load settings from database
        console.log('[AppContext] Loading settings from database')
        await container.settings.load()

        // Check URL for user context (from ScoutITAI)
        const urlParams = new URLSearchParams(window.location.search)
        const userEmail = urlParams.get('user')
        const userName = urlParams.get('username')

        if (userEmail) {
          // User navigated from ScoutITAI - store user context
          console.log('[AppContext] User context from URL:', { userEmail, userName })
          container.userContext = {
            email: userEmail,
            name: userName,
            source: 'scoutitai'
          }
        }

        setSettingsLoaded(true)
      } catch (error) {
        console.error('[AppContext] Failed to load settings:', error)
        setSettingsLoaded(true) // Continue with defaults
      }
    }

    loadSettings()
  }, [container, config])

  // Show loading screen until settings are ready
  if (!settingsLoaded) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: 'var(--bg-primary, #0f172a)',
        color: 'var(--text-primary, #f1f5f9)',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid rgba(59, 130, 246, 0.3)',
            borderTop: '3px solid #3b82f6',
            borderRadius: '50%',
            margin: '0 auto 20px',
            animation: 'spin 1s linear infinite'
          }} />
          <div style={{ fontSize: '14px', opacity: 0.7 }}>Loading RecodeIT...</div>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    )
  }

  return (
    <AppContext.Provider value={{ ...container, settingsLoaded }}>
      {children}
    </AppContext.Provider>
  )
}

/**
 * Hook to access the app container
 *
 * @returns {Object} The container with db, files, api, ai, adapters, environment
 */
export function useApp() {
  const context = useContext(AppContext)

  if (!context) {
    throw new Error('useApp must be used within an AppProvider')
  }

  return context
}

/**
 * Hook to access just the database adapter
 */
export function useDatabase() {
  const { db } = useApp()
  return db
}

/**
 * Hook to access just the file storage adapter
 */
export function useFileStorage() {
  const { files } = useApp()
  return files
}

/**
 * Hook to access just the ModernizeIT API adapter
 */
export function useModernizeAPI() {
  const { api } = useApp()
  return api
}

/**
 * Hook to access the AI adapter
 */
export function useAI() {
  const { ai } = useApp()
  return ai
}

/**
 * Hook to get the current environment
 */
export function useEnvironment() {
  const { environment } = useApp()
  return environment
}

/**
 * Hook to access settings management
 * Returns { getCurrent, load, settingsLoaded }
 */
export function useSettings() {
  const { settings, settingsLoaded } = useApp()
  return { ...settings, settingsLoaded }
}

export default AppContext
